import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, UserPlus, Mail, MapPin } from 'lucide-react';
import departmentService, { Department } from '@/services/departmentService';
import locationService, { Location } from '@/services/locationService';
import type { AiFormMetadata } from '@/scripts/userflow';

interface InviteUserModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: InviteUserFormData) => Promise<void>;
  roles: RoleOption[];
}

export interface InviteUserFormData {
  employeeId?: string;
  email: string;
  firstName: string;
  lastName?: string;
  jobTitle?: string;
  departmentId: string;
  roleId: string;
  locationIds: string[]; // Array of location IDs - Required
}

export interface RoleOption {
  id: string;
  name: string;
}

const InviteUserModal = ({
  open,
  onClose,
  onSubmit,
  roles,
}: InviteUserModalProps) => {
  const [formData, setFormData] = useState<InviteUserFormData>({
    employeeId: '',
    email: '',
    firstName: '',
    lastName: '',
    jobTitle: '',
    departmentId: '',
    roleId: '',
    locationIds: [],
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [departments, setDepartments] = useState<Department[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);

  useEffect(() => {
    if (open) {
      // Reset form when modal opens
      setFormData({
        employeeId: '',
        email: '',
        firstName: '',
        lastName: '',
        jobTitle: '',
        departmentId: '',
        roleId: '',
        locationIds: [],
      });
      setErrors({});
      loadDepartments();
      loadLocations();
    }
  }, [open]);

  const loadDepartments = async () => {
    try {
      const data = await departmentService.getAllDepartments();
      setDepartments(data);
    } catch (error) {
      console.error('Error loading departments:', error);
    }
  };

  const loadLocations = async () => {
    try {
      const data = await locationService.getLocations();
      setLocations(data);
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  const validate = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // First name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters';
    }

    // Last name is optional with no restrictions

    // Department validation
    if (!formData.departmentId) {
      newErrors.departmentId = 'Please select a department';
    }

    // Role validation
    if (!formData.roleId) {
      newErrors.roleId = 'Please select a role';
    }

    // Location validation
    if (!formData.locationIds || formData.locationIds.length === 0) {
      newErrors.locationIds = 'Please select at least one facility';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      await onSubmit(formData);
      onClose();
    } catch (error: any) {
      console.error('Error inviting user:', error);

      let errorMessage = 'Failed to invite user. Please try again.';

      if (error.message) {
        errorMessage = error.message;

        // Check for specific errors
        if (error.message.includes('already exists') || error.message.includes('duplicate')) {
          setErrors({
            email: `A user with email "${formData.email}" already exists.`,
            submit: errorMessage
          });
        } else {
          setErrors({ submit: errorMessage });
        }
      } else {
        setErrors({ submit: errorMessage });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh]">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg border-2 border-blue-300 dark:border-blue-700 flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <DialogTitle>Invite New User</DialogTitle>
              <DialogDescription>
                Send an invitation to join your team
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4 max-h-[calc(90vh-250px)] overflow-y-auto px-1">
            {/* Employee ID */}
            <div className="space-y-2">
              <Label htmlFor="employeeId" className="text-sm font-medium">
                Employee ID <span className="text-muted-foreground text-xs">(Optional)</span>
              </Label>
              <Input
                id="employeeId"
                placeholder="EMP001"
                value={formData.employeeId}
                onChange={(e) =>
                  setFormData({ ...formData, employeeId: e.target.value })
                }
                className={errors.employeeId ? 'border-red-500' : ''}
              />
              {errors.employeeId && (
                <p className="text-xs text-red-500">{errors.employeeId}</p>
              )}
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">
                Email Address <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="john.doe@company.com"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className={`pl-10 ${errors.email ? 'border-red-500' : ''}`}
                />
              </div>
              {errors.email && (
                <p className="text-xs text-red-500">{errors.email}</p>
              )}
            </div>

            {/* First Name & Last Name */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName" className="text-sm font-medium">
                  First Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="firstName"
                  placeholder="John"
                  value={formData.firstName}
                  onChange={(e) =>
                    setFormData({ ...formData, firstName: e.target.value })
                  }
                  className={errors.firstName ? 'border-red-500' : ''}
                />
                {errors.firstName && (
                  <p className="text-xs text-red-500">{errors.firstName}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastName" className="text-sm font-medium">
                  Last Name
                </Label>
                <Input
                  id="lastName"
                  placeholder="Doe"
                  value={formData.lastName}
                  onChange={(e) =>
                    setFormData({ ...formData, lastName: e.target.value })
                  }
                  className={errors.lastName ? 'border-red-500' : ''}
                />
                {errors.lastName && (
                  <p className="text-xs text-red-500">{errors.lastName}</p>
                )}
              </div>
            </div>

            {/* Job Title & Department */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="jobTitle" className="text-sm font-medium">
                  Job Title <span className="text-muted-foreground text-xs">(Optional)</span>
                </Label>
                <Input
                  id="jobTitle"
                  placeholder="ESG Manager"
                  value={formData.jobTitle}
                  onChange={(e) =>
                    setFormData({ ...formData, jobTitle: e.target.value })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="department" className="text-sm font-medium">
                  Department <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.departmentId}
                  onValueChange={(value) =>
                    setFormData({ ...formData, departmentId: value })
                  }
                >
                  <SelectTrigger className={errors.departmentId ? 'border-red-500' : ''}>
                    <SelectValue placeholder="Select a department" />
                  </SelectTrigger>
                  <SelectContent>
                    {departments.map((dept) => (
                      <SelectItem key={dept.id} value={dept.id}>
                        {dept.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.departmentId && (
                  <p className="text-xs text-red-500">{errors.departmentId}</p>
                )}
              </div>
            </div>

            {/* Role */}
            <div className="space-y-2">
              <Label htmlFor="role" className="text-sm font-medium">
                Role <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.roleId}
                onValueChange={(value) =>
                  setFormData({ ...formData, roleId: value })
                }
              >
                <SelectTrigger className={errors.roleId ? 'border-red-500' : ''}>
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role) => (
                    <SelectItem key={role.id} value={role.id}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.roleId && (
                <p className="text-xs text-red-500">{errors.roleId}</p>
              )}
            </div>

            {/* Locations */}
            <div className="space-y-2">
              <Label htmlFor="locations" className="text-sm font-medium">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Locations <span className="text-red-500">*</span>
                </div>
              </Label>
              <div className={`border rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto ${errors.locationIds ? 'border-red-500' : ''}`}>
                {locations.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No facilities available</p>
                ) : (
                  locations.map((location) => (
                    <label
                      key={location.id}
                      className="flex items-start gap-3 p-2 rounded-md hover:bg-muted/50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={formData.locationIds?.includes(location.id) || false}
                        onChange={(e) => {
                          const currentIds = formData.locationIds || [];
                          const newIds = e.target.checked
                            ? [...currentIds, location.id]
                            : currentIds.filter((id) => id !== location.id);
                          setFormData({ ...formData, locationIds: newIds });
                        }}
                        className="mt-0.5 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium">{location.unitCode}</p>
                        {location.address && (
                          <p className="text-xs text-muted-foreground">
                            {[
                              location.address.city,
                              location.address.state,
                              location.address.country,
                            ]
                              .filter(Boolean)
                              .join(', ')}
                          </p>
                        )}
                      </div>
                    </label>
                  ))
                )}
              </div>
              {formData.locationIds && formData.locationIds.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {formData.locationIds.length} location{formData.locationIds.length > 1 ? 's' : ''} selected
                </p>
              )}
              {errors.locationIds && (
                <p className="text-xs text-red-500">{errors.locationIds}</p>
              )}
            </div>

            {errors.submit && (
              <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <p className="text-sm text-red-600 dark:text-red-400">
                  {errors.submit}
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !formData.email.trim() || !formData.firstName.trim() || !formData.departmentId || !formData.roleId || !formData.locationIds?.length}
              className="bg-blue-500 hover:bg-blue-600 text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending Invitation...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Send Invitation
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

/**
 * AI Form Metadata for Invite User Form
 * Exported for use by the conversational AI form assistant
 */
export const INVITE_USER_FORM_METADATA: AiFormMetadata = {
  formId: 'invite_user_form',
  formName: 'Invite User',
  endpoint: '/users/invite',
  method: 'POST',
  fields: [
    {
      name: 'email',
      label: 'Email Address',
      type: 'email',
      required: true,
      helpText: 'The email address of the person you want to invite',
      example: 'john.doe@company.com',
    },
    {
      name: 'firstName',
      label: 'First Name',
      type: 'text',
      required: true,
      helpText: 'Their first name',
      example: 'John',
    },
    {
      name: 'lastName',
      label: 'Last Name',
      type: 'text',
      required: false,
      helpText: 'Their last name',
      example: 'Doe',
    },
    {
      name: 'employeeId',
      label: 'Employee ID',
      type: 'text',
      required: false,
      helpText: 'Their employee ID (if applicable)',
      example: 'EMP001',
    },
    {
      name: 'jobTitle',
      label: 'Job Title',
      type: 'text',
      required: false,
      helpText: 'Their position in your organization',
      example: 'ESG Manager',
    },
    {
      name: 'departmentId',
      label: 'Department',
      type: 'select',
      required: true,
      helpText: 'Select their department',
      dynamicOptionsKey: 'departments',
    },
    {
      name: 'roleId',
      label: 'Assign Role',
      type: 'select',
      required: true,
      helpText: 'Choose which role to assign them',
      dynamicOptionsKey: 'roles',
    },
    {
      name: 'locationIds',
      label: 'Facilities',
      type: 'multi-select',
      required: true,
      helpText: 'Select one or more facilities for this user',
      dynamicOptionsKey: 'locations',
    },
  ],
};

export default InviteUserModal;
